/**
 * The text the program settings page shows for each choice the user has made.
 *
 * Every row on that page names the option the user picked and, underneath it, a
 * sentence about that option. Both pieces of text depend only on the stored
 * value, so they are declared here as one table per kind of choice instead of as
 * a pair of hand-written `switch` statements in the page.
 *
 * Why a table and not a `switch`: a `Record` keyed by an enum requires an entry
 * for every member the enum declares, so adding a new possible answer without
 * writing its text is a compile error here. A `switch` with a `default:` accepts
 * the same omission silently, and the missing text then reaches a real user's
 * screen as a blank row — or, for the training split, as a confidently wrong
 * split name. That is deferred finding `df-355718ce3e55`.
 *
 * The tables live beside the page rather than beside the enums because the
 * enums are in `shared/` and the translation object `LL` is client-only.
 *
 * Every entry is a function, never an already-read string, so no translation is
 * read while this module is being loaded.
 */

import { LL } from 'i18n/i18n.service';
import { TranslatedString, untranslatedString } from 'models/translated-string';
import { ActivityLevel, AdvancedMethodsMyoReps, AdvancedMethodsRMTesting, DedicationLevel, StressLevel } from '@cf-shared/enums';
import { MuscleGrowthMain } from '@cf-shared/data-model/muscle-growth-preference';
import { TrainingSplitMode } from '@cf-shared/data-model/training-split';

/** The two pieces of text one settings row shows: the chosen option, and the sentence under it. */
export interface SettingsRowText {
  /** The name of the option the user picked. */
  value: () => TranslatedString;
  /** The sentence shown under it. Empty where a row deliberately says nothing more. */
  note: () => TranslatedString;
}

/** What a row falls back to when nothing maps the stored value. Both pieces render as nothing. */
export const EMPTY_ROW_TEXT: SettingsRowText = {
  value: () => untranslatedString(''),
  note: () => untranslatedString(''),
};

/**
 * Read one entry out of a table, falling back when the stored value is not in it.
 *
 * The fallback is not decoration. A document read from the database may have
 * been written by an older or a newer build of the app, so the value it holds
 * can be one this build's enum does not contain, or missing altogether. Neither
 * case has an entry in the table, and neither may throw.
 *
 * The own-property test is what makes that promise hold for every stored value
 * rather than most of them. A plain object inherits names like `toString` and
 * `constructor` from its prototype, so indexing it with one of those as a key
 * answers with the inherited function instead of nothing — which would pass a
 * `??` fallback and then throw when the caller read `.value` off it.
 */
export function readTableEntry<TKey extends string | number, TEntry>(
  table: Record<TKey, TEntry>,
  storedValue: TKey | undefined,
  fallback: TEntry,
): TEntry {
  // if there is no stored value at all, nothing can be looked up
  if (storedValue === undefined || storedValue === null) return fallback;
  // if this table does not declare that key itself, fall back rather than inheriting one
  if (!Object.prototype.hasOwnProperty.call(table, storedValue)) return fallback;
  return table[storedValue] ?? fallback;
}

export const MYO_REPS_ROW_TEXT: Record<AdvancedMethodsMyoReps, SettingsRowText> = {
  [AdvancedMethodsMyoReps.Prefer]: {
    value: () => LL.intake.advanced.myoRepsPrefer(),
    note: () => LL.intake.advanced.myoRepsPreferNote(),
  },
  [AdvancedMethodsMyoReps.Avoid]: {
    value: () => LL.intake.advanced.myoRepsAvoid(),
    note: () => LL.intake.advanced.myoRepsAvoidNote(),
  },
  [AdvancedMethodsMyoReps.Automatic]: {
    value: () => LL.intake.advanced.automatic(),
    note: () => LL.intake.advanced.myoRepsAutomaticNote(),
  },
};

export const RM_TESTING_ROW_TEXT: Record<AdvancedMethodsRMTesting, SettingsRowText> = {
  [AdvancedMethodsRMTesting.Prefer]: {
    value: () => LL.intake.advanced.rmTestingPrefer(),
    note: () => LL.intake.advanced.rmTestingPreferNote(),
  },
  [AdvancedMethodsRMTesting.Avoid]: {
    value: () => LL.intake.advanced.rmTestingAvoid(),
    note: () => LL.intake.advanced.rmTestingAvoidNote(),
  },
  [AdvancedMethodsRMTesting.Automatic]: {
    value: () => LL.intake.advanced.automatic(),
    // the automatic option deliberately says nothing further, unlike the other two
    note: () => untranslatedString(''),
  },
};

export const ACTIVITY_LEVEL_ROW_TEXT: Record<ActivityLevel, SettingsRowText> = {
  [ActivityLevel.Sedentary]: {
    value: () => LL.intake.lifestyle.sedentary(),
    note: () => LL.intake.lifestyle.sedentaryNote(),
  },
  [ActivityLevel.SomewhatActive]: {
    value: () => LL.intake.lifestyle.somewhatActive(),
    note: () => LL.intake.lifestyle.somewhatActiveNote(),
  },
  [ActivityLevel.Active]: {
    value: () => LL.intake.lifestyle.active(),
    note: () => LL.intake.lifestyle.activeNote(),
  },
  [ActivityLevel.VeryActive]: {
    value: () => LL.intake.lifestyle.veryActive(),
    note: () => LL.intake.lifestyle.veryActiveNote(),
  },
};

export const STRESS_LEVEL_ROW_TEXT: Record<StressLevel, SettingsRowText> = {
  [StressLevel.MildStress]: {
    value: () => LL.intake.lifestyle.mildStress(),
    note: () => LL.intake.lifestyle.mildStressNote(),
  },
  [StressLevel.Average]: {
    value: () => LL.intake.lifestyle.averageStress(),
    note: () => LL.intake.lifestyle.averageStressNote(),
  },
  [StressLevel.HighStress]: {
    value: () => LL.intake.lifestyle.highStress(),
    note: () => LL.intake.lifestyle.highStressNote(),
  },
};

export const PHYSIQUE_PRIORITY_ROW_TEXT: Record<MuscleGrowthMain, SettingsRowText> = {
  [MuscleGrowthMain.BareMinimum]: {
    value: () => LL.intake.goals.bareMinimum(),
    note: () => LL.intake.goals.bareMinimumNote(),
  },
  [MuscleGrowthMain.Balanced]: {
    value: () => LL.intake.goals.balanced(),
    note: () => LL.intake.goals.balancedNote(),
  },
  [MuscleGrowthMain.Custom]: {
    value: () => LL.intake.goals.custom(),
    note: () => LL.intake.goals.customNote(),
  },
  [MuscleGrowthMain.LowerBody]: {
    value: () => LL.intake.goals.lowerBody(),
    note: () => LL.intake.goals.lowerBodyNote(),
  },
};

/**
 * Used by two rows, not one: how many sets the user trains per week, and how far
 * their calories sit from maintenance. Both ask the same question — how hard the
 * user wants to push — and both showed the same three answers written out twice.
 */
export const DEDICATION_LEVEL_ROW_TEXT: Record<DedicationLevel, SettingsRowText> = {
  [DedicationLevel.SlowAndSteady]: {
    value: () => LL.intake.dedication.slowAndSteady(),
    note: () => LL.intake.dedication.slowAndSteadyNote(),
  },
  [DedicationLevel.Balanced]: {
    value: () => LL.intake.dedication.balanced(),
    note: () => LL.intake.dedication.balancedNote(),
  },
  [DedicationLevel.MaximumEffort]: {
    value: () => LL.intake.dedication.maximumEffort(),
    note: () => LL.intake.dedication.maximumEffortNote(),
  },
};

/**
 * The name of the split the user is on — the value only.
 *
 * There is no sentence under this row to map, so this table holds one function
 * per mode rather than a pair. The page writes that sentence itself, because it
 * depends on how often the user trains rather than on the stored split.
 */
export const TRAINING_SPLIT_VALUE_TEXT: Record<TrainingSplitMode, () => TranslatedString> = {
  [TrainingSplitMode.Optimal]: () => LL.intake.trainingSplit.optimal(),
  [TrainingSplitMode.PushPullLegs]: () => LL.intake.trainingSplit.pushPullLegs(),
  [TrainingSplitMode.UpperLower]: () => LL.intake.trainingSplit.upperLower(),
  [TrainingSplitMode.FullBody]: () => LL.intake.trainingSplit.fullBody(),
};

/**
 * A row whose answer is yes or no rather than one of several options.
 *
 * These get no `Record`: there is no enum to be exhaustive over, so a table buys
 * nothing. What they do get is the condition written once. Before, each of these
 * rows asked the same question twice — once to choose the option name and again
 * to choose the sentence — so the two halves could be changed apart and end up
 * describing different states.
 */
export interface SwitchedRowText {
  /** What the row shows when the condition holds. */
  on: SettingsRowText;
  /** What it shows when the condition does not hold. */
  off: SettingsRowText;
}

/** Read the half of a yes-or-no row that matches the condition. */
export function readSwitchedRowText(text: SwitchedRowText, isOn: boolean): SettingsRowText {
  return isOn ? text.on : text.off;
}

/** The two numbers the Sets Per Week row shows (#6589). */
export interface SetsPerWeekNumbers {
  /** The number the row names as the user's weekly sets target. Undefined when there is none. */
  target: number | undefined;
  /**
   * The number the current workouts were built for, rounded the way the intake form's
   * "Current plan" note rounds it. Set only when it differs from the target, so the row
   * says nothing extra for a user whose two numbers agree.
   */
  currentPlan: number | undefined;
}

/**
 * Work out which sets-per-week numbers the program settings row shows.
 *
 * The app stores two of them. The saved target (`declaredState.trainingVolumeWeeklyAvg`)
 * is what the user picked on the slider. The program's number
 * (`fitnessPlan.trainingVolumeWeeklyAvg`) is what the current workouts were built for, and
 * since #5381 it means exactly that. They differ after the user saves a new target with
 * "Keep My Current Workouts": the workouts, and so the program's number, stay as they were.
 * The row used to show the program's number, so that user kept seeing their old target
 * (#6589). It now names the saved target, and the program's number beside it when the two
 * differ.
 *
 * Either number may be missing or damaged in a document an older build wrote. A saved target
 * that is not a positive number falls back to the program's number with nothing beside it,
 * which is what the row showed before #6589.
 */
export function setsPerWeekNumbers(savedTarget: number | undefined, programNumber: number | undefined): SetsPerWeekNumbers {
  const isUsable = (n: number | undefined): boolean => typeof n === 'number' && Number.isFinite(n) && n > 0;
  // if no usable target was ever saved, show what the program holds, as before #6589
  if (!isUsable(savedTarget)) {
    return { target: isUsable(programNumber) ? programNumber : undefined, currentPlan: undefined };
  }
  // if the program carries no usable number, there is nothing to put beside the target
  if (!isUsable(programNumber)) {
    return { target: savedTarget, currentPlan: undefined };
  }
  const roundedProgram = Math.round(programNumber);
  return {
    target: savedTarget,
    currentPlan: roundedProgram !== Math.round(savedTarget) ? roundedProgram : undefined,
  };
}

export const CALORIE_CYCLING_ROW_TEXT: SwitchedRowText = {
  on: {
    value: () => LL.intake.advanced.calorieCyclingOn(),
    note: () => LL.intake.advanced.calorieCyclingOnNote(),
  },
  off: {
    value: () => LL.intake.advanced.calorieCyclingOff(),
    note: () => LL.intake.advanced.calorieCyclingOffNote(),
  },
};

export const MACRO_TARGETS_ROW_TEXT: SwitchedRowText = {
  on: {
    value: () => LL.intake.nutritionOptions.enabled(),
    note: () => LL.intake.nutritionOptions.enabledNote(),
  },
  off: {
    value: () => LL.intake.nutritionOptions.disabled(),
    note: () => LL.intake.nutritionOptions.disabledNote(),
  },
};

export const MACRO_TRACKING_ROW_TEXT: SwitchedRowText = {
  on: {
    value: () => LL.intake.nutritionOptions.trackInApp(),
    note: () => LL.intake.nutritionOptions.trackInAppNote(),
  },
  off: {
    value: () => LL.intake.nutritionOptions.trackElsewhere(),
    note: () => LL.intake.nutritionOptions.trackElsewhereNote(),
  },
};

export const TRACKING_TYPE_ROW_TEXT: SwitchedRowText = {
  on: {
    value: () => LL.intake.nutritionOptions.flexibleNutrition(),
    note: () => LL.intake.nutritionOptions.flexibleNutritionNote(),
  },
  off: {
    value: () => LL.intake.nutritionOptions.fixedTargets(),
    note: () => LL.intake.nutritionOptions.fixedTargetsNote(),
  },
};
